import { useCallback, useEffect, useRef, useState } from "react";
import { sectionProgress } from "./motion";

export function useScrollScene(update: (progress: number, stage: HTMLDivElement, measure: boolean) => void) {
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const progress = useRef(0);
  const latestUpdate = useRef(update);
  latestUpdate.current = update;
  const [active, setActive] = useState(false);
  const requestMeasure = useRef<(() => void) | null>(null);
  const refresh = useCallback(() => requestMeasure.current?.(), []);

  useEffect(() => {
    const section = root.current;
    const surface = stage.current;
    if (!section || !surface) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let needsMeasure = true;
    let previousProgress = -1;
    const render = () => {
      frame = 0;
      progress.current = preference.matches ? 0 : sectionProgress(section.getBoundingClientRect().top, section.offsetHeight, surface.offsetHeight);
      if (progress.current === previousProgress && !needsMeasure) return;
      const measure = needsMeasure;
      needsMeasure = false;
      previousProgress = progress.current;
      latestUpdate.current(progress.current, surface, measure);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(render); };
    const measure = () => { needsMeasure = true; schedule(); };
    requestMeasure.current = measure;
    const observer = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), { rootMargin: "200px" });
    const sizeObserver = new ResizeObserver(measure);
    observer.observe(section);
    sizeObserver.observe(surface);
    sizeObserver.observe(section);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", measure);
    preference.addEventListener("change", measure);
    render();
    return () => {
      cancelAnimationFrame(frame);
      requestMeasure.current = null;
      observer.disconnect();
      sizeObserver.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", measure);
      preference.removeEventListener("change", measure);
    };
  }, []);

  function goTo(fraction: number) {
    if (!root.current || !stage.current) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const top = root.current.getBoundingClientRect().top + window.scrollY - 80;
    const distance = root.current.offsetHeight - stage.current.offsetHeight;
    window.scrollTo({ top: top + Math.max(0, Math.min(1, fraction)) * distance, behavior: "smooth" });
  }

  return { root, stage, progress, active, goTo, refresh };
}
